import { JwtApiFactory } from '@eloomi/eloomi-users-persona-client/1.0';
import { authorizeClient } from '@/api/http-client';
import { makeLogger } from '@/common/services';
import { JWT } from '../interfaces';
import { JWTLoginSessionStore } from './session-store';

const jwtApiClient = authorizeClient(JwtApiFactory);

export class JWTSessionRefresher {
    private readonly STORAGE_KEY = 'INFINITE_JWT_LAST_REFRESH';
    private readonly REFRESH_PERIOD = 1000 * 60 * 60; // 1 hour

    private readonly log = makeLogger('session-refresher');
    private readonly store = new JWTLoginSessionStore();

    private static instance: JWTSessionRefresher;
    private refreshTimer?: number;
    private failHandler?: () => void;

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private constructor() {}

    public static getInstance(): JWTSessionRefresher {
        if (!JWTSessionRefresher.instance) {
            JWTSessionRefresher.instance = new JWTSessionRefresher();
        }

        return JWTSessionRefresher.instance;
    }

    public start(makeRefreshTimeMark = false) {
        this.startRefresher();
        if (makeRefreshTimeMark) {
            this.markLastRefreshTime();
        }
    }

    public resume(refreshImmediately = false) {
        this.resumeRefresher(refreshImmediately);
    }

    public stop() {
        this.stopRefresher();
    }

    public setFailHandler(handler?: () => void) {
        this.failHandler = handler;
    }

    private async refreshToken() {
        try {
            const jwt = (await jwtApiClient.getToken()).data as JWT;
            this.store.write(jwt);
            this.markLastRefreshTime();

            // Schedule next refresh only if active timer wasn't canceled outside
            // while network request was processed.
            if (this.refreshTimer !== undefined) {
                this.scheduleRefresh();
            }
        } catch (error) {
            this.store.clean();
            this.stopRefresher();

            if (this.failHandler !== undefined) {
                this.failHandler();
            }
        }
    }

    private scheduleRefresh(timeout = this.REFRESH_PERIOD) {
        this.log('session refresh is scheduled in %o ms', timeout);
        this.refreshTimer = window.setTimeout(async () => {
            await this.refreshToken();
        }, timeout);
    }

    private startRefresher() {
        this.stopRefresher();
        this.scheduleRefresh();
    }

    private resumeRefresher(immediate = false) {
        if (immediate) {
            this.stopRefresher();
            this.scheduleRefresh(0);
            return;
        }

        if (this.refreshTimer !== undefined) {
            return;
        }

        const lastRefreshTime = this.getLastRefreshTimeMark();
        if (!lastRefreshTime) {
            this.scheduleRefresh();
            return;
        }

        const timeElapsed = Date.now() - lastRefreshTime;
        const timeout = timeElapsed >= this.REFRESH_PERIOD ? 0 : this.REFRESH_PERIOD - timeElapsed;

        this.scheduleRefresh(timeout);
    }

    private stopRefresher() {
        if (this.refreshTimer !== undefined) {
            window.clearTimeout(this.refreshTimer);
            this.refreshTimer = undefined;
        }
        this.cleanLastRefreshTimeMark();
    }

    private getLastRefreshTimeMark() {
        const timeMark = localStorage.getItem(this.STORAGE_KEY);
        if (timeMark !== null) {
            return Number(timeMark);
        }
        return undefined;
    }

    private markLastRefreshTime() {
        localStorage.setItem(this.STORAGE_KEY, String(Date.now()));
    }

    private cleanLastRefreshTimeMark() {
        localStorage.removeItem(this.STORAGE_KEY);
    }
}
